import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { capturePageview, ensureAnalyticsReady } from "./posthog-client";

/**
 * Loads PostHog after the app is interactive, never during render or on the
 * critical path. Gating (env flag, remote kill switch, idle scheduling) lives
 * in posthog-client.ts so identify()/trackEvent() share the exact same
 * readiness check instead of racing it.
 *
 * Also emits $pageview on route changes: the slim bundle only captures the one
 * for the initial load, so without this every path and drop-off query describes
 * landing pages and nothing else. Only the path counts: filters, `?src=` and
 * other query-string changes stay on the same page.
 */
export const useAnalyticsInit = () => {
  const { pathname } = useLocation();
  const lastPathname = useRef<string | null>(null);

  useEffect(() => {
    ensureAnalyticsReady();
  }, []);

  useEffect(() => {
    // The SDK already sent one for the first path; sending it again would
    // double every landing page in the funnel.
    if (lastPathname.current === null) {
      lastPathname.current = pathname;
      return;
    }
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    capturePageview();
  }, [pathname]);
};
